var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, Stack, TextField } from '@mui/material';
import { ControlPoint, Search } from '@mui/icons-material';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { ReportCard } from '../ReportCard';
import { useLogiLibraryTranslation } from '../../hooks';
/**
 * Represents a Reports Page component.
 * This component will display list of dashboards as {@link ReportCard} components
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {Function} props.getToken - A function that returns a {@link LogiTokenResponse} use to authenticated when embedding.
 * @param {string} props.logiBaseUrl - The base URL for LogiBackend.
 * @param {string} props.openReportLabel - The label for opening a report button for ReportCard.
 * @param {Function} props.onReportOpenClick - A function that should bring user to ReportDetailsPage when the click on open report of ReportCart Component.
 * @param {ReactNode} [props.pageHeader] - The optional page header component.
 * @param {Function} [props.getDashboards] - An optional function that can be your own function to return a list of dashboards {@link LogiDashboardResponse},
 * else will just use getToken function to call logi dashboard list api /composer/api/dashboards with access token
 * @param {ReportCardMenuItem[]} [props.reportCardMenuItems] - option menu items to have on each card. {@link ReportCardMenuItem}, ex. could include edit menu item to open editable emebdded dashboard
 * @param {boolean} props.allowCreateReport - Shows create report button if true
 *  @example
 * ```tsx
 * <ReportsPage
 *    getToken={getToken}
 *    logiBaseUrl={logiBaseUrl}
 *    pageHeader={pageHeader}
 *    getDashboards={getDashboards}
 *    onReportOpenClick={handleReportOpenClick}
 *    openReportLabel={openReportLabel}
 *    reportCardMenuItems={reportCardMenuItems}
 *    allowCreateReport={true}
 * />
 * ```
 */
export const ReportsPage = ({ getToken, logiBaseUrl, pageHeader, getDashboards, onReportOpenClick, openReportLabel, reportCardMenuItems, allowCreateReport, }) => {
    const [dashboards, setDashboards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const { t } = useLogiLibraryTranslation();
    const defaultGetDashboards = () => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield getToken();
        const dashboardsListUrl = logiBaseUrl + '/composer/api/dashboards';
        const access_token = result.access_token;
        const headers = {
            Accept: 'application/vnd.composer.v3+json',
            'Content-Type': 'application/vnd.composer.v3+json',
            Authorization: 'Bearer ' + access_token,
        };
        return (yield (yield fetch(dashboardsListUrl, { headers })).json()).content;
    });
    useEffect(() => {
        (() => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            const response = getDashboards ? yield getDashboards() : yield defaultGetDashboards();
            setDashboards(response);
            setLoading(false);
        }))();
    }, []);
    return (React.createElement(React.Fragment, null,
        pageHeader && pageHeader,
        React.createElement(Stack, { bgcolor: themeColors.surface.primary, padding: 2, marginY: 3, marginX: 2, borderRadius: 1, border: 1, spacing: 2, borderColor: themeColors.border.main, direction: 'row', alignItems: 'center', sx: { display: { sm: 'block', lg: 'flex' }, justifyContent: 'space-between' } },
            React.createElement(Box, { sx: {
                    display: 'grid',
                    flexGrow: '1',
                    gap: '0.5rem',
                    '@media (min-width: 640px)': {
                        display: 'flex',
                    },
                } },
                React.createElement(TextField, { sx: { width: '50%' }, size: 'small', placeholder: t('Search') || '', value: search, onChange: (event) => setSearch(event.target.value), InputProps: {
                        startAdornment: (React.createElement(InputAdornment, { position: 'start' },
                            React.createElement(Search, null))),
                    } })),
            allowCreateReport && (React.createElement(Button, { size: 'large', variant: 'contained', color: 'primary', startIcon: React.createElement(ControlPoint, null) }, t('Create Report')))),
        React.createElement(Box, { sx: {
                padding: '20px',
                gap: 5,
                display: 'grid',
                '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
            } },
            loading && React.createElement(CircularProgress, null),
            !loading &&
                dashboards.map((dashboard) => {
                    return (React.createElement(ReportCard, { key: dashboard.id, report: dashboard, onReportOpenClick: () => onReportOpenClick(dashboard), openReportLabel: openReportLabel, menuItems: reportCardMenuItems }));
                }))));
};
